import { useContext } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { useMutation, useQueryClient } from 'react-query';
import { toast } from 'react-toastify';
import { v4 } from 'uuid';
import Button from '../../../components/Button/Button';
import FormActions from '../../../components/Form/FormActions';
import Input from '../../../components/Form/Input';
import InputRow from '../../../components/Form/InputRow';
import Select from '../../../components/Form/Select';
import useAppState from '../../../hooks/useAppState/useAppState';
import { UserBookie } from '../../../types/models';
import bookieDetail from '../../../utils/bookieDetail';
import format from '../../../utils/format';
import AccountContext from '../AccountContext';
import styles from './Transfer.module.css';

type FormValues = {
  to: string;
  amount: string;
};

export default function Deposit() {
  const queryClient = useQueryClient();
  const { balance, card, myBookies } = useContext(AccountContext);
  const { axios } = useAppState();
  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
  } = useForm<FormValues>();
  const watchValue = watch();
  const transferMutation = useMutation<
    unknown,
    {
      response: {
        data: {
          error: {
            errorNumber: number;
            message: string;
          };
        };
      };
    },
    FormValues
  >(
    async (data) => {
      const bookie = JSON.parse(data.to);

      await axios.post(`/v1/userbookies/${bookie.id}/transfer`, {
        tid: v4(),
        action: 'deposit-from-bc',
        amount: +data.amount,
      });
    },
    {
      onSuccess: () => {
        toast.success('Your deposit was successful.');
      },
      onSettled: () => {
        setTimeout(() => {
          queryClient.refetchQueries('Cards');
          queryClient.refetchQueries('BookiesBalance');
        }, 1000);
      },
      onError: (error) => {
        toast.error('Something went wrong with your deposit.');
      },
    }
  );

  const transfer: SubmitHandler<FormValues> = async (data) => {
    await transferMutation.mutate(data);
  };

  const bookie: UserBookie = JSON.parse(watchValue.to || '{}');

  return (
    <form className={styles.content} onSubmit={handleSubmit(transfer)}>
      <InputRow label="From">
        <span>
          {card?.cardNumber} ({format.currency(card?.balance)})
        </span>
      </InputRow>
      <InputRow label="To" errorText={errors.to?.message}>
        <Select
          id="To"
          options={[
            {
              label: 'Select a bookie',
              value: '',
            },
            ...myBookies
              .filter((userBookie) => userBookie.status === 'active')
              .map((userBookie) => ({
                label: `${
                  bookieDetail[userBookie.bookieId].name
                } (${format.currency(
                  balance.find((b) => userBookie.bookieId === b.bookieId)
                    ?.withdrawableBalance
                )})`,
                value: JSON.stringify(userBookie),
              })),
          ]}
          {...register('to', { required: 'Required' })}
        />
      </InputRow>
      <InputRow label="Amount" errorText={errors.amount?.message}>
        <Input
          type="number"
          min={0}
          {...register('amount', {
            required: 'Required',
            min: {
              value: 1,
              message: `Minimum deposit amount is ${format.currency(1)}.`,
            },
            max: {
              value: card?.balance || 0,
              message: `You cannot exceed your Bookies Card balance (${format.currency(
                card?.balance
              )}).`,
            },
          })}
        />
      </InputRow>
      <FormActions>
        <Button type="submit" disabled={transferMutation.isLoading}>
          {watchValue.to
            ? `Deposit from Bookies Card to ${
                bookieDetail?.[bookie.bookieId]?.name
              }`
            : 'Deposit'}
        </Button>
      </FormActions>
    </form>
  );
}
